import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InlineError } from "@/components/ListState";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { plural } from "@/lib/plural";

const EIGEN_BV_GEEN = "__geen__";

// Owner-only: the sender BV, the BTW rate, and the invoice numbering start.
// Numbering is the one setting that locks forever once the first invoice is
// issued (round 27 §2).
export const SettingsFacturatie = () => {
  const [afz, setAfz] = useState(null);
  const [num, setNum] = useState(null);
  const [entities, setEntities] = useState([]);
  const [error, setError] = useState("");
  const [savingA, setSavingA] = useState(false);
  const [savingN, setSavingN] = useState(false);

  const load = async () => {
    setError("");
    try {
      const [a, n] = await Promise.all([
        api.get("/settings/afzender"),
        api.get("/settings/factuurnummering"),
      ]);
      api.get("/entities").then(({ data }) => setEntities(data)).catch(() => {});
      setAfz({ statutaire_naam: "", adres: "", postcode: "", plaats: "", land: "NL", kvk: "", btw_nummer: "", iban: "", betaaltermijn_dagen: 30, btw_percentage: 21, entity_id: null, ...a.data });
      setNum({ prefix: n.data.prefix || "", volgend_nummer: n.data.volgend_nummer || "", locked: n.data.locked, eerste_nummer: n.data.eerste_nummer, pad: n.data.pad || 4 });
    } catch (e) { setError(getErrorMessage(e, "Kan facturatie-instellingen niet laden.")); }
  };
  useEffect(() => { load(); }, []);

  const saveAfz = async () => {
    setSavingA(true);
    try {
      const { data } = await api.put("/settings/afzender", {
        ...afz,
        betaaltermijn_dagen: Number(afz.betaaltermijn_dagen) || 30,
        btw_percentage: Number(String(afz.btw_percentage).replace(",", ".")) || 0,
      });
      toast.success("Afzendergegevens opgeslagen");
      // S6: a batch can go to SEVERAL debiteuren, and some bonnen end up with
      // none — one name for all of them would be a claim, not a report.
      const mee = data?.debiteur_meegegaan || [];
      const perDoel = {};
      mee.forEach((m) => {
        const key = m.naar_naam || (m.naar ? m.naar : null);
        perDoel[key || "__geen__"] = (perDoel[key || "__geen__"] || 0) + 1;
      });
      Object.entries(perDoel).forEach(([doel, n]) => {
        if (doel === "__geen__") {
          toast.warning(`${plural(n, "werkbon heeft", "werkbonnen hebben")} nu geen debiteur`);
        } else {
          toast.success(`${plural(n, "werkbon gaat", "werkbonnen gaan")} mee naar ${doel}`);
        }
      });
      const opConcept = data?.debiteur_op_concept || [];
      if (opConcept.length) {
        toast.warning(`${plural(opConcept.length, "werkbon staat", "werkbonnen staan")} op een concept en gaat niet mee`);
      }
      await load();
    } catch (e) { toast.error(getErrorMessage(e, "Opslaan mislukt.")); }
    finally { setSavingA(false); }
  };

  const saveNum = async () => {
    const n = parseInt(num.volgend_nummer, 10);
    if (!n || n < 1) { toast.error("Geef een geldig volgend nummer (1 of hoger)."); return; }
    setSavingN(true);
    try {
      const { data } = await api.put("/settings/factuurnummering", { prefix: num.prefix || "", volgend_nummer: n });
      setNum({ ...num, ...data });
      toast.success("Nummering vastgelegd");
    } catch (e) { toast.error(getErrorMessage(e, "Opslaan mislukt.")); }
    finally { setSavingN(false); }
  };

  if (error) return <InlineError testid="facturatie-load-error" message={error} onRetry={load} />;
  if (!afz || !num) return <p className="text-body text-muted-foreground">Laden...</p>;

  const preview = `${num.prefix || ""}${String(parseInt(num.volgend_nummer, 10) || 0).padStart(num.pad || 4, "0")}`;

  return (
    <div className="space-y-4" data-testid="settings-facturatie">
      <Card className="p-5">
        <h2 className="mb-1 text-body font-semibold text-foreground">Afzender (Truckwash 1 Techniek & Beheer BV)</h2>
        <p className="mb-4 text-data text-muted-foreground">Verschijnt bovenaan elke factuur. Eén BTW-tarief voor de hele groep.</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2"><Label>Statutaire naam</Label><Input data-testid="afzender-naam-input" value={afz.statutaire_naam} onChange={(e) => setAfz((f) => ({ ...f, statutaire_naam: e.target.value }))} /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Adres</Label><Input data-testid="afzender-adres-input" value={afz.adres} onChange={(e) => setAfz((f) => ({ ...f, adres: e.target.value }))} /></div>
          <div className="space-y-1.5"><Label>Postcode</Label><Input data-testid="afzender-postcode-input" value={afz.postcode} onChange={(e) => setAfz((f) => ({ ...f, postcode: e.target.value }))} /></div>
          <div className="space-y-1.5"><Label>Plaats</Label><Input data-testid="afzender-plaats-input" value={afz.plaats} onChange={(e) => setAfz((f) => ({ ...f, plaats: e.target.value }))} /></div>
          <div className="space-y-1.5"><Label>KvK</Label><Input data-testid="afzender-kvk-input" value={afz.kvk} onChange={(e) => setAfz((f) => ({ ...f, kvk: e.target.value }))} /></div>
          <div className="space-y-1.5"><Label>BTW-nummer</Label><Input data-testid="afzender-btw-input" value={afz.btw_nummer} onChange={(e) => setAfz((f) => ({ ...f, btw_nummer: e.target.value }))} /></div>
          <div className="space-y-1.5"><Label>IBAN</Label><Input data-testid="afzender-iban-input" value={afz.iban} onChange={(e) => setAfz((f) => ({ ...f, iban: e.target.value }))} /></div>
          <div className="space-y-1.5"><Label>Betaaltermijn (dagen)</Label><Input type="number" data-testid="afzender-betaaltermijn-input" value={afz.betaaltermijn_dagen} onChange={(e) => setAfz((f) => ({ ...f, betaaltermijn_dagen: e.target.value }))} /></div>
          <div className="space-y-1.5"><Label>BTW-tarief (%)</Label><Input data-testid="afzender-btw-percentage-input" value={afz.btw_percentage} onChange={(e) => setAfz((f) => ({ ...f, btw_percentage: e.target.value }))} /></div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>Welke entiteit is je eigen BV (de afzender)?</Label>
            <Select value={afz.entity_id || EIGEN_BV_GEEN} onValueChange={(v) => setAfz((f) => ({ ...f, entity_id: v === EIGEN_BV_GEEN ? null : v }))}>
              <SelectTrigger data-testid="afzender-eigen-bv-select"><SelectValue placeholder="Niet gekozen" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={EIGEN_BV_GEEN}>Niet gekozen</SelectItem>
                {entities.map((e) => <SelectItem key={e.entity_id} value={e.entity_id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-data text-muted-foreground">
              Werkbonnen van een asset of locatie die aan deze entiteit hangt gaan naar de debiteur van hun locatie — aan je eigen BV factureer je niet. Werkbonnen die er nu al op staan gaan direct mee.
            </p>
          </div>
        </div>
        <div className="mt-3"><Button data-testid="afzender-opslaan-button" onClick={saveAfz} disabled={savingA}>{savingA ? "Bezig..." : "Afzender opslaan"}</Button></div>
      </Card>

      <Card className="p-5">
        <h2 className="mb-1 text-body font-semibold text-foreground">Factuurnummering</h2>
        <p className="mb-4 text-data text-muted-foreground">
          De nummering loopt door op de reeks die je nu al gebruikt. Vul het volgende nummer één keer in.
          {" "}<strong>Zodra de eerste factuur is uitgegeven, staat dit vast.</strong>
        </p>
        {num.locked ? (
          <div className="rounded border border-lijn-zacht bg-muted/30 p-3 text-body text-foreground" data-testid="nummering-locked">
            De nummering staat vast sinds factuur <span className="font-mono">{num.eerste_nummer}</span> is uitgegeven en kan niet meer wijzigen.
            Corrigeer een fout met een creditnota.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Voorvoegsel</Label><Input data-testid="nummering-prefix-input" value={num.prefix} onChange={(e) => setNum((f) => ({ ...f, prefix: e.target.value }))} placeholder="2026-" /></div>
              <div className="space-y-1.5"><Label>Volgend nummer</Label><Input type="number" data-testid="nummering-volgend-input" value={num.volgend_nummer} onChange={(e) => setNum((f) => ({ ...f, volgend_nummer: e.target.value }))} placeholder="144" /></div>
            </div>
            <div className="mt-3 text-body text-foreground">Voorbeeld eerstvolgende factuur: <span className="font-mono font-semibold" data-testid="nummering-preview">{preview}</span></div>
            <div className="mt-3"><Button data-testid="nummering-opslaan-button" onClick={saveNum} disabled={savingN}>{savingN ? "Bezig..." : "Nummering vastleggen"}</Button></div>
          </>
        )}
      </Card>
    </div>
  );
};
